<template>
  <div class="w-full">
    <div class="mb-2">
      <AppInputLabel v-if="label" :name="'addressLocation'" :label="label" :hasError="false" :required="true" />
      <div class="relative w-full address-lookup-container">
        <span class="absolute inset-y-0 left-0 flex items-center pl-2">
          <i class="fas fa-search text-normal-reduced"></i>
        </span>
        <input
          ref="addressSearch"
          class="w-full pl-8 address-lookup-input"
          :class="{ 'ios-input': isIOS }"
          type="search"
          v-model="searchText"
          :placeholder="placeholder"
          @blur="handleBlur"
          @focus="handleFocus"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
        />
      </div>
    </div>
    <div v-if="innerAddress.location?._lat && showSelected">
      <AppAddressDisplay :address="innerAddress" :showName="true" />
    </div>
  </div>
</template>
<!-- eslint-disable indent -->
<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from "vue";
import { Address } from "@models/Address";
import AppInputLabel from "@/components/AppInputLabel.vue";
import useDeviceDetection from "@/modules/useDeviceDetection";

const emit = defineEmits(["change"]);

interface Props {
  address: Address;
  label?: string;
  showSelected: boolean;
  isMedium: boolean;
  placeholder: string;
}

const props = defineProps<Props>();
const autocomplete = ref();
const addressSearch = ref<HTMLInputElement | null>(null);
const searchText = ref("");

// Detect iOS platform
const [Platform] = useDeviceDetection();
const isIOS = computed(() => Platform.value === "ios");

const initalAddress = props.address
  ? { ...props.address }
  : {
    addressLine1: "",
    city: "",
    state: "",
    postalCode: "",
    location: null,
  } as Address;

const innerAddress = ref<Address>(initalAddress);

function fillInAddress() {
  console.log('[AddressLookup] fillInAddress called', {
    componentName: props.label || 'unnamed',
    autocompleteExists: !!autocomplete.value,
    isIOS: isIOS.value
  });

  try {
    const place = autocomplete.value.getPlace();
    console.log('[AddressLookup] Got place from autocomplete', {
      componentName: props.label || 'unnamed',
      placeExists: !!place,
      placeName: place?.name
    });

    // On iOS, force blur the input to release focus
    if (isIOS.value && addressSearch.value) {
      addressSearch.value.blur();
    }

    let streetNumber = "";
    let route = "";
    innerAddress.value = {
      addressLine1: "",
      city: "",
      state: "",
      postalCode: "",
      location: null,
    } as Address;

    if (!place) {
      console.log('[AddressLookup] No place returned from autocomplete', {
        componentName: props.label || 'unnamed'
      });
      return;
    }

    for (const addressComponent of place.address_components) {
      const addressType = addressComponent.types[0];
      switch (addressType) {
        case "intersection":
          streetNumber = addressComponent.short_name;
          break;
        case "street_number":
          streetNumber = addressComponent.short_name;
          break;
        case "route":
          route = addressComponent.short_name;
          break;
        case "locality":
          innerAddress.value.city = addressComponent.long_name;
          break;
        case "administrative_area_level_1":
          innerAddress.value.state = addressComponent.short_name;
          break;
        case "postal_code":
          innerAddress.value.postalCode = addressComponent.short_name;
          break;
        case "country":
          innerAddress.value.countryCode = addressComponent.short_name;
          break;
        default:
          break;
      }
    }

    if (place.geometry?.location && innerAddress.value) {
      innerAddress.value.location = {
        _lat: place.geometry?.location.lat(),
        _long: place.geometry?.location.lng(),
      };
    }

    searchText.value = "";
    innerAddress.value.addressLine1 = `${streetNumber} ${route}`.trim();
    innerAddress.value.name =
      innerAddress.value.addressLine1 !== place.name ? place.name : null;

    // Update searchText based on showSelected prop
    if (!props.showSelected) {
      searchText.value = place.formatted_address || `${innerAddress.value.addressLine1}, ${innerAddress.value.city}, ${innerAddress.value.state}`;
    }

    emit("change", innerAddress.value);

  } catch (error) {
    console.error('[AddressLookup] Error in fillInAddress:', error, {
      componentName: props.label || 'unnamed'
    });
  }
}

function initAutocomplete() {
  console.log('[AddressLookup] Initializing autocomplete', {
    addressSearchRef: addressSearch.value,
    componentName: props.label || 'unnamed'
  });

  // Check if Google Maps API is available
  if (typeof window.google === 'undefined' || !window.google.maps || !window.google.maps.places) {
    console.error('[AddressLookup] Google Maps API not available', {
      componentName: props.label || 'unnamed',
      google: typeof window.google !== 'undefined',
      googleMaps: typeof window.google !== 'undefined' && !!window.google.maps,
      googleMapsPlaces: typeof window.google !== 'undefined' && !!window.google.maps && !!window.google.maps.places
    });
    return;
  }

  try {
    // Check if the DOM element is available
    if (!addressSearch.value) {
      console.error('[AddressLookup] Address search DOM element not available', {
        componentName: props.label || 'unnamed'
      });
      return;
    }

    // Try to use the new PlaceAutocompleteElement if available
    if (window.google.maps.places.PlaceAutocompleteElement) {
      console.log('[AddressLookup] Using new PlaceAutocompleteElement API', {
        componentName: props.label || 'unnamed'
      });

      try {
        // Create a new PlaceAutocompleteElement
        const placeAutocompleteElement = new window.google.maps.places.PlaceAutocompleteElement({
          inputElement: addressSearch.value,
          types: ["establishment", "geocode"]
        });

        // Add event listener for place_changed
        placeAutocompleteElement.addListener("place_changed", () => {
          console.log('[AddressLookup] PlaceAutocompleteElement place_changed event triggered', {
            componentName: props.label || 'unnamed'
          });

          const place = placeAutocompleteElement.getPlace();
          if (place) {
            console.log('[AddressLookup] Got place from PlaceAutocompleteElement', {
              componentName: props.label || 'unnamed',
              placeName: place.name
            });

            // Store the place in the autocomplete object for fillInAddress to use
            autocomplete.value = {
              getPlace: () => place
            };

            fillInAddress();
          }
        });

        console.log('[AddressLookup] PlaceAutocompleteElement initialized successfully', {
          componentName: props.label || 'unnamed'
        });

        return;
      } catch (error) {
        console.error('[AddressLookup] Error initializing PlaceAutocompleteElement, falling back to legacy Autocomplete:', error, {
          componentName: props.label || 'unnamed'
        });
      }
    }

    // Fall back to legacy Autocomplete
    console.log('[AddressLookup] Using legacy Autocomplete API', {
      componentName: props.label || 'unnamed'
    });

    // Create the autocomplete object, restricting the search predictions to
    // geographical location types.
    // eslint-disable-next-line no-undef
    autocomplete.value = new window.google.maps.places.Autocomplete(addressSearch.value, {
      types: ["establishment", "geocode"],
    });

    console.log('[AddressLookup] Autocomplete created successfully', {
      autocompleteObj: !!autocomplete.value,
      componentName: props.label || 'unnamed'
    });

    // Avoid paying for data that you don't need by restricting the set of
    // place fields that are returned to just the address components.
    // autocomplete.setFields(["address_component"]);
    autocomplete.value.setFields([
      "address_component",
      "formatted_address",
      "geometry",
      "name",
    ]);

    // When the user selects an address from the drop-down, populate the
    // address fields in the form.
    autocomplete.value.addListener("place_changed", () => {
      console.log('[AddressLookup] place_changed event triggered', {
        componentName: props.label || 'unnamed'
      });
      fillInAddress();
    });

    console.log('[AddressLookup] Autocomplete initialization complete', {
      componentName: props.label || 'unnamed'
    });
  } catch (error) {
    console.error('[AddressLookup] Error initializing autocomplete:', error, {
      componentName: props.label || 'unnamed'
    });
  }
}

function geolocate() {
  console.log('[AddressLookup] geolocate called', {
    componentName: props.label || 'unnamed',
    autocompleteExists: !!autocomplete.value,
    isCurrentLocation: props.address?.name === 'Current Location'
  });

  const localAutocomplete = autocomplete.value;
  // Check if autocomplete exists and if we're not in "Current Location" mode
  if (!localAutocomplete || props.address?.name === 'Current Location') {
    console.log('[AddressLookup] Skipping geolocate - autocomplete not available or using current location', {
      componentName: props.label || 'unnamed'
    });
    return;
  }

  if (navigator.geolocation) {
    console.log('[AddressLookup] Getting current position', {
      componentName: props.label || 'unnamed'
    });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('[AddressLookup] Got position', {
          componentName: props.label || 'unnamed',
          coords: {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
        });

        const geolocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        try {
          if (typeof window.google === 'undefined' || !window.google.maps) {
            console.error('[AddressLookup] Google Maps API not available for setting bounds', {
              componentName: props.label || 'unnamed'
            });
            return;
          }

          // eslint-disable-next-line no-undef
          const circle = new window.google.maps.Circle({
            center: geolocation,
            radius: position.coords.accuracy,
          });
          localAutocomplete.setBounds(circle.getBounds());
          console.log('[AddressLookup] Set bounds for autocomplete', {
            componentName: props.label || 'unnamed'
          });
        } catch (error) {
          console.error('[AddressLookup] Error setting bounds:', error, {
            componentName: props.label || 'unnamed'
          });
        }
      },
      (error) => {
        console.error('[AddressLookup] Geolocation error:', error, {
          componentName: props.label || 'unnamed'
        });
      }
    );
  } else {
    console.log('[AddressLookup] Geolocation not available', {
      componentName: props.label || 'unnamed'
    });
  }
}

const isCurrentLocation = computed(() => props.address?.name === 'Current Location');

const handleFocus = (event: FocusEvent) => {
  console.log('[AddressLookup] Input focused', {
    componentName: props.label || 'unnamed',
    autocompleteExists: !!autocomplete.value,
    isCurrentLocation: isCurrentLocation.value,
    isIOS: isIOS.value
  });

  const input = event.target as HTMLInputElement;

  // On iOS, don't select all text as this can cause focus issues
  if (!isIOS.value) {
    input.select();
  }

  if (!isCurrentLocation.value) {
    console.log('[AddressLookup] Calling geolocate()', {
      componentName: props.label || 'unnamed'
    });
    geolocate();
  }
};

const handleBlur = (event: FocusEvent) => {
  console.log('[AddressLookup] Input blur', {
    componentName: props.label || 'unnamed',
    isIOS: isIOS.value
  });

  const input = event.target as HTMLInputElement;

  // On iOS, we need to ensure the selection is properly cleared
  // and the focus is completely released
  if (isIOS.value) {
    // Force blur on iOS by setting readonly temporarily
    input.setAttribute('readonly', 'readonly');
    setTimeout(() => {
      input.removeAttribute('readonly');
    }, 50);
  } else {
    input.setSelectionRange(0, 0);
  }
};

watch(() => props.address, (newAddress) => {
  console.log('[AddressLookup] Address prop changed', {
    componentName: props.label || 'unnamed',
    newAddress,
    autocompleteExists: !!autocomplete.value
  });

  if (newAddress?.name === 'Current Location') {
    console.log('[AddressLookup] Setting to Current Location mode', {
      componentName: props.label || 'unnamed'
    });
    searchText.value = 'Current Location';
    // Prevent autocomplete from initializing when using current location
    if (autocomplete.value) {
      console.log('[AddressLookup] Clearing autocomplete for Current Location', {
        componentName: props.label || 'unnamed'
      });

      try {
        if (typeof window.google !== 'undefined' && window.google.maps && window.google.maps.event) {
          // eslint-disable-next-line no-undef
          window.google.maps.event.clearInstanceListeners(autocomplete.value);
        }
      } catch (error) {
        console.error('[AddressLookup] Error clearing instance listeners:', error, {
          componentName: props.label || 'unnamed'
        });
      }

      autocomplete.value = null;
    }
  } else if (newAddress?.name) {
    console.log('[AddressLookup] Setting search text to address name', {
      componentName: props.label || 'unnamed',
      name: newAddress.name
    });
    searchText.value = newAddress.name;
    if (!autocomplete.value) {
      console.log('[AddressLookup] Initializing autocomplete from watch', {
        componentName: props.label || 'unnamed'
      });
      initAutocomplete();
    }
  } else {
    console.log('[AddressLookup] Address changed but no name property', {
      componentName: props.label || 'unnamed',
      newAddress
    });
  }
}, { deep: true });


onUnmounted(() => {
  if (autocomplete.value) {
    try {
      if (typeof window.google !== 'undefined' && window.google.maps && window.google.maps.event) {
        // eslint-disable-next-line no-undef
        window.google.maps.event.clearInstanceListeners(autocomplete.value);
      }
    } catch (error) {
      console.error('[AddressLookup] Error clearing instance listeners on unmount:', error, {
        componentName: props.label || 'unnamed'
      });
    }

    autocomplete.value = null;
  }
});

onMounted(() => {
  console.log('[AddressLookup] Component mounted', {
    componentName: props.label || 'unnamed',
    addressProp: props.address,
    addressSearchRef: addressSearch.value,
    googleAvailable: typeof google !== 'undefined',
    googleMapsAvailable: typeof google !== 'undefined' && !!google.maps,
    googleMapsPlacesAvailable: typeof google !== 'undefined' && !!google.maps && !!google.maps.places
  });

  // Try to initialize immediately if Google is available
  if (typeof google !== 'undefined' && google.maps && google.maps.places && addressSearch.value) {
    console.log('[AddressLookup] Google Maps API available immediately, initializing', {
      componentName: props.label || 'unnamed'
    });
    searchText.value = props.address?.name ?? "";
    initAutocomplete();
    return;
  }

  // If Google Maps API isn't available yet, try again after a delay
  const checkAndInitInterval = setInterval(() => {
    console.log('[AddressLookup] Checking for Google Maps API', {
      componentName: props.label || 'unnamed',
      googleAvailable: typeof google !== 'undefined',
      googleMapsAvailable: typeof google !== 'undefined' && !!google.maps,
      googleMapsPlacesAvailable: typeof google !== 'undefined' && !!google.maps && !!google.maps.places,
      addressSearchAvailable: !!addressSearch.value
    });

    if (typeof google !== 'undefined' && google.maps && google.maps.places && addressSearch.value) {
      console.log('[AddressLookup] Google Maps API now available, initializing', {
        componentName: props.label || 'unnamed'
      });
      clearInterval(checkAndInitInterval);
      searchText.value = props.address?.name ?? "";
      initAutocomplete();
    }
  }, 500);

  // Clear interval on component unmount
  onUnmounted(() => {
    clearInterval(checkAndInitInterval);
  });
});

</script>

<style>
/* Ensure Google Places Autocomplete dropdown appears above other elements, including modals */
.pac-container {
  z-index: 999999 !important;
}

/* Style the dropdown items for better visibility */
.pac-item {
  padding: 8px !important;
  cursor: pointer !important;
}

/* iOS-specific styles to improve touch handling */
@supports (-webkit-touch-callout: none) {
  .address-lookup-input {
    /* Prevent iOS zoom on focus */
    font-size: 16px !important;
    /* Improve touch target size */
    min-height: 44px;
    /* Prevent iOS from applying its own styles */
    -webkit-appearance: none;
    appearance: none;
    /* Prevent text selection */
    -webkit-user-select: none;
    user-select: none;
    /* Prevent iOS tap highlight */
    -webkit-tap-highlight-color: transparent;
  }

  /* Force hardware acceleration for better performance */
  .address-lookup-container {
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    will-change: transform;
  }
}

/* Specific class for iOS devices */
.ios-input {
  /* Prevent iOS keyboard from taking over */
  position: relative;
  z-index: 1;
  /* Prevent iOS from stealing focus */
  touch-action: manipulation;
  /* Ensure proper touch behavior */
  cursor: pointer;
}
</style>